/**
 * Security Sanitization Utilities
 * Protects against XSS, script injection, and invalid protocol schemes.
 */

const DANGEROUS_TAGS_REGEX = /<\s*(script|iframe|object|embed|style|meta|link|svg|math)[^>]*>.*?<\s*\/\s*\1\s*>|<\s*(script|iframe|object|embed|style|meta|link|svg|math)[^>]*\/?>/gi
const INLINE_EVENT_HANDLERS_REGEX = /on[a-z]+\s*=\s*(?:'[^']*'|"[^"]*"|[^\s>]+)/gi
const JAVASCRIPT_PSEUDO_PROTOCOL_REGEX = /javascript\s*:/gi

/**
 * Sanitizes plain text input by stripping executable tags and dangerous inline handlers.
 */
export function sanitizeTextInput(input: string | undefined | null, maxLength = 2000): string {
  if (!input || typeof input !== 'string') return ''

  // Fast path: ordinary plain text without HTML tags, pseudo protocols or event handlers
  const hasTag = input.indexOf('<') !== -1
  const hasColon = input.indexOf(':') !== -1
  const hasOn = /on[a-z]/i.test(input)

  let sanitized: string
  if (!hasTag && !hasColon && !hasOn) {
    sanitized = input.trim()
  } else {
    sanitized = input
      .replace(DANGEROUS_TAGS_REGEX, '')
      .replace(INLINE_EVENT_HANDLERS_REGEX, '')
      .replace(JAVASCRIPT_PSEUDO_PROTOCOL_REGEX, '')
      .trim()
  }

  if (maxLength > 0 && sanitized.length > maxLength) {
    sanitized = sanitized.slice(0, maxLength)
  }

  return sanitized
}

/**
 * Validates and sanitizes an image or external URL.
 * Only allows safe protocols: https://, http://, data:image/, or local /images/ paths.
 */
export function sanitizeUrl(url: string | undefined | null): string {
  if (!url || typeof url !== 'string') return ''

  const trimmed = url.trim()
  if (!trimmed) return ''

  // Allowed safe prefixes
  if (
    trimmed.startsWith('https://') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('data:image/') ||
    trimmed.startsWith('/') ||
    !trimmed.includes(':') // Plain filename like 'Apfel.webp'
  ) {
    // Ensure no nested javascript: or encoded script triggers
    if (/javascript\s*:/i.test(trimmed) || /data:text\//i.test(trimmed)) {
      return ''
    }
    return trimmed
  }

  return ''
}

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

/**
 * Validates standard email address syntax.
 */
export function isValidEmail(email: string | undefined | null): boolean {
  if (!email || typeof email !== 'string') return false
  const clean = email.trim()
  if (clean.length < 5 || clean.length > 254) return false
  return EMAIL_REGEX.test(clean)
}
